/**
 * The far tier of glints over the water, on a 2D canvas of its own.
 *
 * Kept off the shader's canvas so the glints stay crisp at the screen's
 * full resolution while the water behind them may render below it, and
 * so they still shine where WebGL is unavailable. The transforms mirror
 * the app's Skia group exactly: translate to the box corner, scale by
 * the span, then rotate inside the unit box — the glint pivots on the
 * box's corner, not its centre.
 */

import { colors } from '@config/colors';
import { starStates, type StageMetrics } from './water-field';

/** The handoff's four-point sparkle, in a unit box. */
const GLINT = 'M0.5 0 L0.62 0.38 L1 0.5 L0.62 0.62 L0.5 1 L0.38 0.62 L0 0.5 L0.38 0.38 Z';

/** A full-screen 2D canvas at 2× on a 4K window is 128 MB; past this it renders softer instead. */
const MAX_PIXELS = 4_000_000;
const MAX_DPR = 2;

export type WaterStars = {
  resize(cssWidth: number, cssHeight: number): void;
  draw(t: number, metrics: StageMetrics): void;
};

export function createWaterStars(canvas: HTMLCanvasElement): WaterStars | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const glint = new Path2D(GLINT);
  let pxPerCss = 1;

  return {
    resize(cssWidth, cssHeight) {
      if (cssWidth <= 0 || cssHeight <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const scale = Math.min(dpr, Math.sqrt(MAX_PIXELS / (cssWidth * cssHeight)));
      const w = Math.max(1, Math.round(cssWidth * scale));
      const h = Math.max(1, Math.round(cssHeight * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      pxPerCss = w / cssWidth;
    },
    draw(t, metrics) {
      const k = metrics.unit * pxPerCss;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of starStates(t, metrics.W, metrics.H, metrics.stars)) {
        if (s.alpha <= 0) continue;
        ctx.globalAlpha = Math.min(1, s.alpha);
        if (s.glint) {
          ctx.setTransform(k, 0, 0, k, 0, 0);
          ctx.translate(s.x - s.span / 2, s.y - s.span / 2);
          ctx.scale(s.span, s.span);
          ctx.rotate(s.rotation);
          ctx.fillStyle = colors.wee.glint;
          ctx.fill(glint);
        } else {
          ctx.setTransform(k, 0, 0, k, k * s.x, k * s.y);
          const disc = ctx.createRadialGradient(0, 0, 0, 0, 0, s.span);
          disc.addColorStop(0, colors.wee.glintSoft);
          disc.addColorStop(1, colors.wee.glintClear);
          ctx.fillStyle = disc;
          ctx.beginPath();
          ctx.arc(0, 0, s.span, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    },
  };
}
